import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Modal, message, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import EditableTable from '@components/EditableTable';
import api from '@services/api';
import { getModelDataColumns } from '../modelDataInvoice';
import useNavigateToActionsMsg from '../NavigateToActionsMsg';

interface IEditItemsOnInvoice {
  modalVisible: boolean;
  onClose: () => void;
  itemToEdit: any;
  customerId: string | undefined;
  invoiceId: string | undefined;
}

const EditItemsOnInvoice = ({
  modalVisible,
  onClose,
  itemToEdit,
  customerId,
  invoiceId,
}: IEditItemsOnInvoice) => {
  const { t } = useTranslation('invoices');

  const [isSaving, setIsSaving] = useState(false);
  const [temporaryItem, setTemporaryItem] = useState(itemToEdit);

  const updateItem = useCallback(() => {
    const { itemId, description, quantity, value } = temporaryItem;
    return api.post('/service-proxy/broker/invoice-actions', {
      actions: [
        {
          itemId,
          description,
          quantity,
          value,
          customerId,
          invoiceId,
        },
      ],
      type: 'EDIT_ITEM',
    });
  }, [customerId, invoiceId, temporaryItem]);

  useEffect(() => {
    if (itemToEdit && modalVisible) {
      setTemporaryItem({
        itemId: itemToEdit.idItemFatura,
        description: itemToEdit.dsItem?.replaceAll('Extra - ', ''),
        quantity: itemToEdit.nuQtdItem,
        price: itemToEdit.nuValorUnitario,
        totalValue: itemToEdit.nuValorTotal,
        blItemEdit: itemToEdit.blItemEdit,
      });
    }
  }, [itemToEdit, modalVisible]);

  const dataModelColumns = useMemo(() => {
    const columns = getModelDataColumns(t);
    columns.shift();
    columns.shift();
    return columns;
  }, [t]);

  const { navigateToActionsMsg } = useNavigateToActionsMsg();

  const handleItemUpdate = useCallback(async () => {
    try {
      setIsSaving(true);

      const { data } = await updateItem();
      navigateToActionsMsg(data.operationProtocol);
      setTemporaryItem(undefined);
      onClose();
    } catch {
      message.error(t('invoiceItem.msgs.error-to-updating'));
    }
    setIsSaving(false);
  }, [updateItem, navigateToActionsMsg, onClose, t]);

  const handleEditableTableChange = useCallback(
    (items: any) => {
      setTemporaryItem(items[0]);
    },
    [setTemporaryItem],
  );

  return (
    <Modal
      cancelText={t('items-modal.buttons.cancel')}
      maskClosable={false}
      style={{ maxWidth: 800 }}
      title={`${t('items-modal.edit-items')} (${itemToEdit?.dsItem})`}
      open={modalVisible}
      width="70%"
      centered
      afterClose={() => document.body.style.removeProperty('overflow')}
      onCancel={onClose}
      footer={
        <>
          <Button onClick={onClose}>{t('items-modal.buttons.cancel')}</Button>
          <Button loading={isSaving} type="primary" onClick={handleItemUpdate}>
            {t('items-modal.buttons.confirm')}
          </Button>
        </>
      }
    >
      <EditableTable
        columns={dataModelColumns}
        data={[temporaryItem]}
        onChange={handleEditableTableChange}
        tableKey="editItems"
        allowDelete
      />
    </Modal>
  );
};

export default EditItemsOnInvoice;
